import api from "../services/api";
import { CLASSIC_TOKENS, CLASSIC_SLUG } from "./fallback/classicTokens";

/**
 * Obtención del tema activo (doc 0390, FT-4.3).
 *
 * REGLA INNEGOCIABLE: esta función NUNCA rechaza. La aplicación no puede quedarse
 * sin estilos porque la red falle, el servidor tarde o la sesión no exista
 * todavía. Ante cualquier problema devuelve la instantánea de `classic`, que es
 * exactamente lo que el producto se ve hoy.
 *
 * ORDEN DE PREFERENCIA
 *
 *   1. caché local     — pinta al instante, sin esperar a la red
 *   2. servidor        — refresca en segundo plano y actualiza la caché
 *   3. instantánea     — si no hay ni caché ni servidor
 */

const CLAVE_CACHE = "wtx.theme.v1";

const instantanea = () => ({
  themeSlug: CLASSIC_SLUG,
  userMode: null,
  sidebarCollapsed: false,
  tokens: CLASSIC_TOKENS,
  origen: "fallback"
});

export function leerCache() {
  try {
    const crudo = window.localStorage.getItem(CLAVE_CACHE);
    if (!crudo) return null;

    const datos = JSON.parse(crudo);

    // Una caché sin tokens es peor que ninguna: dejaría la interfaz sin valores
    // y sin motivo aparente. Se descarta y se cae a la instantánea.
    if (!datos || !Array.isArray(datos.tokens) || datos.tokens.length === 0) {
      return null;
    }

    return { ...datos, origen: "cache" };
  } catch (e) {
    return null;
  }
}

export function guardarCache(tema) {
  try {
    window.localStorage.setItem(
      CLAVE_CACHE,
      JSON.stringify({
        themeSlug: tema.themeSlug,
        userMode: tema.userMode,
        sidebarCollapsed: tema.sidebarCollapsed,
        tokens: tema.tokens
      })
    );
  } catch (e) {
    // Sin espacio o en modo privado: seguir sin caché es perfectamente válido.
  }
}

export function limpiarCache() {
  try {
    window.localStorage.removeItem(CLAVE_CACHE);
  } catch (e) {
    /* nada que hacer */
  }
}

/**
 * Lo que se pinta de inmediato, sin esperar a la red: caché si la hay,
 * instantánea si no. Siempre síncrono.
 */
export function temaInicial() {
  return leerCache() || instantanea();
}

/**
 * Pide el tema al servidor. Nunca lanza.
 *
 * @returns {Promise<{tokens:Array, themeSlug:string, origen:string}>}
 */
export async function obtenerTema() {
  try {
    const { data } = await api.get("/theme");

    if (!data || !Array.isArray(data.tokens) || data.tokens.length === 0) {
      return temaInicial();
    }

    const tema = {
      themeId: data.themeId,
      themeSlug: data.themeSlug,
      userMode: data.userMode,
      sidebarCollapsed: Boolean(data.sidebarCollapsed),
      tokens: data.tokens,
      origen: "servidor"
    };

    guardarCache(tema);
    return tema;
  } catch (e) {
    // 401 antes de iniciar sesión es el caso normal, no un error: en la pantalla
    // de acceso todavía no hay usuario del que resolver preferencia.
    return temaInicial();
  }
}

/** Persiste la preferencia de claro/oscuro. Nunca lanza. */
export async function guardarModo(mode) {
  try {
    await api.put("/theme/mode", { mode });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Temas que esta empresa puede elegir. Nunca lanza: si falla, devuelve lista
 * vacía y el selector simplemente no ofrece opciones.
 */
export async function listarTemas() {
  try {
    const { data } = await api.get("/theme/available");
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}

/**
 * Cambia el tema de la empresa. A diferencia del resto, esta SÍ propaga el error:
 * es una acción explícita de una persona sobre una pantalla, y fallar en silencio
 * le haría creer que el cambio se guardó cuando no fue así.
 *
 * Devuelve el tema ya resuelto para aplicarlo sin una segunda vuelta.
 */
export async function guardarTemaEmpresa(themeSlug) {
  await api.put("/theme/company", { themeSlug });
  limpiarCache();
  return obtenerTema();
}

/**
 * Personaliza el color de acento de la empresa.
 *
 * Si el tema activo es del sistema, el backend lo duplica y activa la copia antes
 * de escribir — aquí no hay que orquestar nada. Propaga el error, como
 * `guardarTemaEmpresa`: es una acción explícita de una persona.
 */
export async function guardarAcento(mode, color) {
  const { data } = await api.put("/theme/accent", { mode, color });
  limpiarCache();
  const tema = await obtenerTema();
  return { ...tema, creado: Boolean(data && data.created) };
}

export default {
  temaInicial,
  obtenerTema,
  guardarModo,
  listarTemas,
  guardarTemaEmpresa,
  guardarAcento,
  limpiarCache
};
